import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, TrendingUp, FileDown, Save } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface Improvement {
  section: string;
  original: string;
  optimized: string;
  reason: string;
}

interface ResumeComparisonProps {
  originalContent: any;
  optimizedContent: any;
  improvements: Improvement[];
  atsScoreBefore: number;
  atsScoreAfter: number;
  keywordsAdded: string[];
  onDownload?: () => void;
  onSave?: () => void;
}

export function ResumeComparison({
  originalContent,
  optimizedContent,
  improvements,
  atsScoreBefore,
  atsScoreAfter,
  keywordsAdded,
  onDownload,
  onSave,
}: ResumeComparisonProps) {
  const scoreImprovement = atsScoreAfter - atsScoreBefore;
  const improvementPercentage = ((scoreImprovement / atsScoreBefore) * 100).toFixed(0);

  return (
    <div className="space-y-6">
      {/* Score Banner */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-background">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold mb-1">Your Optimized Resume</h2>
              <p className="text-muted-foreground">ATS compatibility significantly improved</p>
            </div>
            <Badge variant="secondary" className="text-lg py-2 px-4">
              1 credit used
            </Badge>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex-1">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold">{atsScoreBefore}</span>
                <TrendingUp className="h-6 w-6 text-green-600" />
                <span className="text-3xl font-bold text-green-600">{atsScoreAfter}</span>
                <span className="text-lg text-muted-foreground ml-2">
                  (+{scoreImprovement} points, +{improvementPercentage}%)
                </span>
              </div>
              <Progress 
                value={atsScoreAfter} 
                className="h-3"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Side-by-Side Comparison */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Original</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold mb-2">Summary</h3>
              <p className="text-muted-foreground line-clamp-3">{originalContent.summary}</p>
            </div>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2">Experience</h3>
              {originalContent.experience?.slice(0, 2).map((exp: any, idx: number) => (
                <div key={idx} className="mb-3">
                  <p className="font-medium text-xs">{exp.title}</p>
                  <p className="text-xs text-muted-foreground mb-1">{exp.company}</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    {exp.bullets?.slice(0, 2).map((bullet: string, bidx: number) => (
                      <li key={bidx} className="line-clamp-2">{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-primary/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Optimized
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold mb-2">Summary</h3>
              <p className="line-clamp-3">{optimizedContent.summary}</p>
            </div>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2">Experience</h3>
              {optimizedContent.experience?.slice(0, 2).map((exp: any, idx: number) => (
                <div key={idx} className="mb-3">
                  <p className="font-medium text-xs">{exp.title}</p>
                  <p className="text-xs text-muted-foreground mb-1">{exp.company}</p>
                  <ul className="list-disc list-inside space-y-1">
                    {exp.bullets?.slice(0, 2).map((bullet: string, bidx: number) => (
                      <li key={bidx} className="line-clamp-2 font-medium">{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Improvements List */}
      <Card>
        <CardHeader>
          <CardTitle>Improvements Made ({improvements.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {improvements.slice(0, 5).map((improvement, idx) => (
              <div key={idx} className="border-l-4 border-primary pl-4 py-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{improvement.section}</Badge>
                      <span className="text-xs text-muted-foreground">{improvement.reason}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Before:</p>
                        <p className="line-clamp-2 opacity-60">{improvement.original}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">After:</p>
                        <p className="line-clamp-2 font-medium">{improvement.optimized}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {improvements.length > 5 && (
              <p className="text-sm text-center text-muted-foreground">
                +{improvements.length - 5} more improvements...
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Keywords Added */}
      {keywordsAdded.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Keywords Added ({keywordsAdded.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {keywordsAdded.map((keyword, idx) => (
                <Badge key={idx} variant="secondary">{keyword}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-4 justify-center">
        <Button size="lg" variant="outline" onClick={onDownload}>
          <FileDown className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
        <Button size="lg" onClick={onSave}>
          <Save className="h-4 w-4 mr-2" />
          Save & Continue
        </Button>
      </div>
    </div>
  );
}
